import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { checkTokenValid } from '../token';
import { matchRefererWithFDLApps } from '@/fdl';
import SignIn from '@/pages/SignIn';
import SignUpPage from '@/pages/SignUp';
import SendActivationPage from '@/pages/SendActivation';
import WaitActivationPage from '@/pages/WaitActivation';
import ForgetPasswordPage from '@/pages/ForgetPassword';
import WaitForgetPasswordPage from '@/pages/WaitForgetPassword';
import ChooseAuthMethodPage from '@/pages/ChooseAuthMethod';
import UserInfoPage from '@/pages/UserInfo';
import TwoFactorAuthPage from '@/pages/TwoFactorAuth';
import TwoFactorAuthVerifyPage from '@/pages/TwoFactorAuthVerify';
import GoogleTwoFactorVerifyPage from '@/pages/GoogleTwoFactorVerify';
import WaitDisable2FAPage from '@/pages/WaitDisable2FA';
import UserLoginHistoryPage from '@/pages/UserLoginHistory';
import UserNotificationPage from '@/pages/UserNotification';
import EmailSubscriptionPage from '@/pages/EmailSubscription';
import ManagementPage from '@/pages/SystemConfig';
import OAuthManagementPage from '@/pages/OAuthManagement';
import OAuthApplicationDetailPage from '@/pages/OAuthApplicationDetail';
import EntraIdConfigPage from '@/pages/EntraIdConfig';
import OauthAuthorize from '@/components/OauthAuthorize';
import Robot from '@/components/RobotComponent';
import LoginTokenVerifyPage from '@/pages/LoginTokenVerify';
import WaitChangeEmailPage from '@/pages/WaitChangeEmail';
import ChangeEmailPage from '@/pages/ChangeEmail';
import EntraIdVerifyResultPage from '@/pages/EntraIdVerifyResult';
import EntraSignInPage from '@/pages/EntraSignIn';
import PasskeyPage from '@/pages/Passkey';
import CookieSettingPage from '@/pages/CookieSetting';
import OAuthErrorPage from '@/pages/OAuthError';
import { logger } from '@/utils/logger';


const routes = [
  {
    path: '/users/sign_in',
    name: 'SignIn',
    component: SignIn,
    beforeEnter: async (to, from, next) => {
      const referer = document.referrer;
      logger.log(referer);
      const appId = await matchRefererWithFDLApps(referer);
      if (appId) {
        localStorage.setItem('appId', appId);
        next();
      } else {
        next();
      }
    }
  },
  {
    path: '/users/sign_up',
    name: 'SignUp',
    component: SignUpPage,
  },
  {
    path: '/users/entra_sign_in',
    name: 'EntraSignIn',
    component: EntraSignInPage,
  },
  {
    path: '/users/activation/create',
    name: 'SendActivation',
    component: SendActivationPage,
  },
  {
    path: '/users/activation/wait',
    name: 'WaitActivation',
    component: WaitActivationPage,
  },
  {
    path: '/users/change_email/wait',
    name: 'WaitChangeEmail',
    component: WaitChangeEmailPage,
  },
  {
    path: '/users/password/forget',
    name: 'ForgetPassword',
    component: ForgetPasswordPage,
  },
  {
    path: '/users/password/forget/wait',
    name: 'WaitForgetPassword',
    component: WaitForgetPasswordPage,
  },
  {
    path: '/users/change_email/verify',
    name: 'ChangeEmail',
    component: ChangeEmailPage,
    beforeEnter: async (to, from, next) => {
      if (!to.query.token || !to.query.pre) {
        return next('/users/sign_in');
      }
      const { tokenValid, auth_type } = await checkTokenValid(to.query.pre, to.query.token);
      if (!tokenValid) {
        alert("Sorry, your link has expired. Please request a new one.");
        return next(to.query.pre === 'resetpwd' ? '/users/password/forget' : '/users/sign_in');
      }
      logger.log(auth_type)
      next();
    }
  },
  {
    path: '/users/password/set',
    name: 'SetPassword',
    component: ChooseAuthMethodPage,
    beforeEnter: async (to, from, next) => {
      if (!to.query.token || !to.query.pre) {
        return next('/users/sign_in');
      }
      const { tokenValid, auth_type } = await checkTokenValid(to.query.pre, to.query.token);
      if (!tokenValid) {
        alert("Sorry, your link has expired. Please request a new one.");
        return next(to.query.pre === 'resetpwd' ? '/users/password/forget' : '/users/sign_in');
      }
      logger.log(auth_type)
      next();
    }
  },
  {
    path: '/users/password/reset',
    name: 'ResetPasswordPage',
    component: ChooseAuthMethodPage,
    beforeEnter: async (to, from, next) => {
      if (to.query.verified) {
        return next(); // 如果URL中有verified參數，直接通過
      }
      if (!to.query.token || !to.query.pre) {
        return next('/users/sign_in');
      }
      const fdlAppId = to.query._fdl_app_id;
      if (fdlAppId && fdlAppId.trim() !== '' && fdlAppId !== 'null') {
        localStorage.setItem('redirectAppId', fdlAppId);
      }
      const { tokenValid, auth_type } = await checkTokenValid(to.query.pre, to.query.token);
      if (!tokenValid) {
        alert("Sorry, your link has expired. Please request a new one.");
        return next('/users/password/forget');
      }
      if (auth_type === 0){
        next({
          path: '/2fa/google-otp-verify',
          query: {
            token: to.query.token, // 從當前的query中取得token
            pre: to.query.pre // 從當前的query中取得pre
          }
        });
      } else {
        next();
      }
    }
  },
  {
    path: '/user',
    name: 'UserInfoPage',
    component: UserInfoPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/user/passkey',
    name: 'PasskeyPage',
    component: PasskeyPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/user/two-factor-auth',
    name: 'TwoFactorAuthPage',
    component: TwoFactorAuthPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/2fa/google-otp-verify',
    name: 'GoogleTwoFactorVerifyPage',
    component: GoogleTwoFactorVerifyPage,
  },
  {
    path: '/2fa/otp',
    name: 'Otp2FA',
    component: TwoFactorAuthVerifyPage,
  },
  {
    path: '/2fa/email',
    name: 'Email2FA',
    component: TwoFactorAuthVerifyPage,
    beforeEnter: (to, from, next) => {
      if ((to.query.pre && to.query.token) || to.query.to_email) {
        next();
      } else {
        next('/users/sign_in');
      }
    }
  },
  {
    path: '/user/2fa/disable/wait',
    name: 'WaitDisable2FA',
    component: WaitDisable2FAPage,
  },
  {
    path: '/access_token_verify',
    name: 'LoginTokenVerify',
    component: LoginTokenVerifyPage,
    beforeEnter: (to, from, next) => {
      if (to.query.access_token){
        next();
      } else {
        next('/users/sign_in');
      }
    }
  },
  {
    path: '/user/login_history',
    name: "UserLoginHistoryPage",
    component: UserLoginHistoryPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/user/notification',
    name: 'UserNotificationPage',
    component: UserNotificationPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/user/subscription',
    name: 'EmailSubscriptionPage',
    component: EmailSubscriptionPage,
    meta: { requiresAuth: true },
  },
  {
    path: '/user/management',
    name: 'ManagementPage',
    component: ManagementPage,
    meta: { requiresAuth: true, requiresStaff: true },
  },
  {
    path: '/user/oauth-management',
    name: 'OAuthManagementPage',
    component: OAuthManagementPage,
    meta: { requiresAuth: true, requiresStaff: true },
  },
  {
    path: '/user/oauth-management/:id',
    name: 'OAuthApplicationDetailPage',
    component: OAuthApplicationDetailPage,
    meta: { requiresAuth: true, requiresStaff: true },
  },
  {
    path: '/user/entra_id',
    name: 'EntraIdConfigPage',
    component: EntraIdConfigPage,
    meta: { requiresAuth: true, requiresEntraID: true },
  },
  {
    path: '/robot',
    name: 'RobotEndpoint',
    component: Robot,
  },
  {
    path: '/cookie-settings',
    name: 'CookieSetting',
    component: CookieSettingPage,
  },
  {
    path: '/oauth2/authorize',
    name: 'OauthAuthorizeEndpoint',
    component: OauthAuthorize,
  },
  {
    path: '/oauth/error',
    name: 'OAuthError',
    component: OAuthErrorPage,
  },
  {
    path: '/entra_id/verify_result',
    name: 'EntraIdVerifyResultPage',
    component: EntraIdVerifyResultPage,
    beforeEnter: (to, from, next) => {
      if (to.query) {
        next();
      } else {
        next('/users/sign_in');
      }
    }
  },
  {
    path: '/oauth2/logout',
    name: 'OauthLogoutEndpoint',
    component: OauthAuthorize,
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore();
      const accessToken = to.query.access_token;

      try {
        const requestedUri = to.query.logout_redirect_uri
          ? decodeURIComponent(to.query.logout_redirect_uri)
          : null;
        const result = await authStore.revokeAccessToken(accessToken, requestedUri);
        authStore.purgeUser();
        if (result.allowedLogoutRedirectUri) {
          window.location.href = result.allowedLogoutRedirectUri;
        } else {
          next('/users/sign_in');
        }
      } catch (error) {
        logger.error('Logout error:', error);
        next(false);
      }
    }
  },
  { path: '/:pathMatch(.*)*', redirect: '/users/sign_in' },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title || 'Zyxel Account';
  const authStore = useAuthStore();

  // 檢查 URL fragment 是否包含 #go="[page_name]"
  // 先解碼以處理特殊字元 (例如 " 被編碼成 %22)
  const hash = decodeURIComponent(window.location.hash);
  const goMatch = hash.match(/#go="([^"]+)"/); // 現在可以在解碼後的 hash 上進行匹配
  if (goMatch && goMatch[1]) {
    const targetPage = goMatch[1];
    localStorage.setItem('postLoginRedirect', targetPage);
    // 使用 history.replaceState 清除 hash，可以避免頁面重新加載或產生新的歷史紀錄
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  const fdlAppId = to.query._fdl_app_id;
  if (fdlAppId && fdlAppId.trim() !== '' && fdlAppId !== 'null') {
    localStorage.setItem('redirectAppId', fdlAppId);
  }

  if (to.path === '/oauth2/authorize') {
    const queryParams = ['client_id', 'redirect_uri', 'response_type', 'state'];
    queryParams.forEach(param => {
      if (to.query[param]) {
        localStorage.setItem(param, to.query[param]);
      }
    });

    //In order to solve the problem that Safari cannot get the referrer url
    if(to.query.redirect_uri){
      const appId = await matchRefererWithFDLApps(to.query.redirect_uri);
      if (appId) {
        localStorage.setItem('appId', appId);
      }
    }

    if (!authStore.isAuthenticated) {
      return next('/users/sign_in');
    }else{
      await authStore.fetchCurrentUser();
    }

    //to check login success
    if(!authStore.isLoggedin){
      authStore.purgeUser();
      return next('/users/sign_in');
    }

    authStore.getAuthorizationCodeAndRedirect();
    return next();
  }
  if (authStore.isAuthenticated && !authStore.current_user) {
    if (to.path === '/oauth2/logout') {
      //This change fixes the session timeout problem.
      const requestedUri = to.query.logout_redirect_uri
        ? decodeURIComponent(to.query.logout_redirect_uri)
        : null;
      const result = await authStore.revokeAccessToken(to.query.access_token, requestedUri);
      authStore.purgeUser();
      if (result.allowedLogoutRedirectUri) {
        window.location.href = result.allowedLogoutRedirectUri;
        return;
      }
      return next('/users/sign_in');
    }
    await authStore.fetchCurrentUser();
  }
  if (to.path === '/users/sign_in' && authStore.isLoggedin) {
    authStore.getAuthorizationCodeAndRedirect();
    return next();
  }
  if (to.matched.some(record => record.meta.requiresAuth) && !authStore.isLoggedin) {
    authStore.purgeUser();
    return next('/users/sign_in');
  }

  if (to.matched.some(record => record.meta.requiresEntraID)) {
    if (authStore.current_user.entra_id_eligible === false){
      return next({ name: 'UserInfoPage' });
    }
  }

  if (to.matched.some(record => record.meta.requiresStaff)) {
    if (!authStore.is_staff_verified) {
      return next({ name: 'UserInfoPage' });
    }
  }

  next();
});

export default router;
